// ============================================================================
// ENGAGEMENT REOPEN MODAL
// ============================================================================
// Compact modal-md. Captures a mandatory reason and confirms with a danger
// ConfirmDialog. On reopen the backend:
//   - stamps reversed_* on the active closure history row
//   - restores engagement.status_id from pre_closure_status_id
//   - clears closure_basis / closure_logged_amount / closure_variance / total_wip_amount
//
// Once reopened, timesheet entries on this engagement become editable again
// (the closed-engagement entry guard naturally lifts).

import React, { useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { useReopenEngagement } from 'hooks/api/useEngagement';
import type { Engagement, ReopenEngagementRequest } from 'api/types';

interface EngagementReopenModalProps {
  isOpen: boolean;
  engagement: Engagement | null;
  onClose: () => void;
  onReopened?: (updated: Engagement) => void;
}

function formatDate(s?: string | null): string {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${String(d.getDate()).padStart(2,'0')} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

function formatCurrency(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `₹ ${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const EngagementReopenModal: React.FC<EngagementReopenModalProps> = ({
  isOpen,
  engagement,
  onClose,
  onReopened,
}) => {
  const { reopenEngagement, loading: saving } = useReopenEngagement();
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reasonError, setReasonError] = useState<string | undefined>();

  useEffect(() => {
    if (!isOpen) return;
    setReason('');
    setReasonError(undefined);
  }, [isOpen]);

  const handleAttempt = () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setReasonError('Reopen reason is required');
      return;
    }
    setReasonError(undefined);
    setConfirmOpen(true);
  };

  const handleConfirmed = async () => {
    if (!engagement) return;
    const request: ReopenEngagementRequest = {
      reason: reason.trim(),
      rowVersion: engagement.rowVersion,
    };
    try {
      const updated = await reopenEngagement(engagement.id, request);
      setConfirmOpen(false);
      onReopened?.(updated);
      onClose();
      notificationService.toast.success(`Engagement ${engagement.code} reopened.`);
    } catch (err: any) {
      setConfirmOpen(false);
      const apiMsg =
        err?.response?.data?.message ||
        err?.message ||
        'Unable to reopen the engagement. Please try again.';
      notificationService.toast.error(apiMsg, 6000);
    }
  };

  if (!isOpen || !engagement) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-md" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            Reopen Engagement &mdash; {engagement.code}
          </h2>
          <button className="modal-close" onClick={onClose} disabled={saving}>
            <IconSystem.x size={20} />
          </button>
        </div>
        <div className="modal-content" style={{ overflow: 'auto', flex: 1 }}>
          <div
            style={{
              padding: '0.6rem 0.8rem',
              background: 'var(--bg-secondary, rgba(99,102,241,0.06))',
              borderRadius: 6,
              marginBottom: '0.8rem',
              fontSize: '0.85rem',
              color: 'var(--text-secondary)',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              rowGap: '0.25rem',
              columnGap: '1rem',
            }}
          >
            <span>Client / Type / Year</span>
            <span style={{ textAlign: 'right' }}>
              {engagement.clientName} — {engagement.engagementTypeName} — {engagement.year}
            </span>
            <span>Closed On</span>
            <span style={{ textAlign: 'right' }}>{formatDate(engagement.closedDate)}</span>
            <span>Closure Basis</span>
            <span style={{ textAlign: 'right' }}>{engagement.closureBasis || '—'}</span>
            <span>Locked Amount (basis)</span>
            <span style={{ textAlign: 'right' }}>{formatCurrency(engagement.closureLoggedAmount)}</span>
            <span>WIP Variance</span>
            <span
              style={{
                textAlign: 'right',
                color:
                  (engagement.closureVariance ?? 0) < 0
                    ? '#dc2626'
                    : (engagement.closureVariance ?? 0) > 0
                    ? '#16a34a'
                    : 'inherit',
                fontWeight: 600,
              }}
            >
              {formatCurrency(engagement.closureVariance)}
            </span>
          </div>

          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.6rem' }}>
            Reopening will restore the engagement to its prior status, clear the locked WIP snapshot,
            and stamp the closure-history row as reversed. Timesheet entries on this engagement
            will become editable again.
          </p>

          <div className="form-group">
            <label className="form-label">
              Reopen Reason <span className="text-danger">*</span>
            </label>
            <textarea
              className={`form-input${reasonError ? ' form-field-error' : ''}`}
              value={reason}
              onChange={(e) => { setReason(e.target.value); if (reasonError) setReasonError(undefined); }}
              disabled={saving}
              rows={3}
              style={{ resize: 'vertical' }}
              placeholder="e.g. Invoice number was wrong; need to revise."
            />
            {reasonError && <div className="form-error-message">{reasonError}</div>}
          </div>
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleAttempt}
            disabled={saving}
          >
            <IconSystem.refresh size={16} /> Reopen Engagement
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Confirm Reopen"
        message={`Reopen engagement ${engagement.code}? This stamps the closure as reversed and unlocks the engagement for further edits.`}
        confirmLabel="Reopen"
        cancelLabel="Back"
        variant="danger"
        loading={saving}
        loadingMessage="Reopening engagement..."
        onConfirm={handleConfirmed}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default EngagementReopenModal;
